import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../home/components/Footer';

const platformResults = [
  {
    platform: 'Wildberries',
    icon: 'ri-shopping-bag-line',
    before: 1500000,
    after: 3200000,
    growth: '+113%',
  },
  {
    platform: 'Ozon',
    icon: 'ri-store-2-line',
    before: 800000,
    after: 2100000,
    growth: '+162%',
  },
  {
    platform: 'Яндекс Маркет',
    icon: 'ri-map-pin-line',
    before: 500000,
    after: 1400000,
    growth: '+180%',
  },
];

const maxValue = Math.max(...platformResults.flatMap(r => [r.before, r.after]));

const systemActions = [
  {
    icon: 'ri-search-line',
    title: 'Глубокая аналитика',
    desc: 'Провели анализ конкурентов и ниши, выявили точки потери трафика и «узкие места» в воронке продаж на каждой из трёх площадок.',
  },
  {
    icon: 'ri-file-text-line',
    title: 'SEO и Контент',
    desc: 'Собрали новое SEO-ядро и оптимизировали карточки товаров (тексты + инфографика), чтобы попасть в точные запросы алгоритмов маркетплейсов.',
  },
  {
    icon: 'ri-calculator-line',
    title: 'Юнит-экономика',
    desc: 'Пересчитали финансовую модель, чтобы масштабировать только прибыльные товары, отсекая неликвид и убыточные позиции.',
  },
  {
    icon: 'ri-advertisement-line',
    title: 'Рекламная стратегия',
    desc: 'Настроили внутреннюю рекламу с постоянным контролем ставок (Bid Management) на всех трёх площадках одновременно.',
  },
];

const formatNum = (n: number) =>
  new Intl.NumberFormat('ru-RU').format(n);

export default function CaseHealthSnackPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-premium-navy relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 border-2 border-premium-gold/20 rounded-full -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 border-2 border-premium-gold/20 rounded-full -ml-36 -mb-36"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-premium-gold/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <Link
            to="/#cases"
            className="inline-flex items-center gap-2 text-premium-gold hover:text-white transition-colors mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            <span className="text-sm font-medium font-body">Назад к кейсам</span>
          </Link>

          <div className="inline-block bg-premium-gold text-premium-navy px-5 py-2 rounded-full text-base font-bold mb-6 shadow-lg font-body">
            📦 Проект на Маркетплейсах: Wildberries, Ozon, Яндекс Маркет
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight font-display">
            Масштабирование бренда снеков<br />в нише FMCG
          </h1>
          <p className="text-premium-gold/80 text-xl mt-2 mb-10 font-body">
            Производитель продуктов питания (Снеки для здорового питания) • 3 маркетплейса • Срок: 3 месяца
          </p>

          {/* Key stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'ri-store-line', label: 'Площадки', value: '3 маркетплейса' },
              { icon: 'ri-time-line', label: 'Срок работы', value: '3 месяца' },
              { icon: 'ri-line-chart-line', label: 'Рост WB', value: '+113%' },
              { icon: 'ri-rocket-line', label: 'Рост Яндекс', value: '+180%' },
            ].map((stat, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-premium-gold/20 hover:border-premium-gold/40 transition-all duration-300">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                  <i className={`${stat.icon} text-2xl text-premium-gold`}></i>
                </div>
                <div className="text-2xl font-bold text-white font-display">{stat.value}</div>
                <div className="text-premium-gold/70 text-sm mt-1 font-body">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">

          {/* Challenge & Context */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-premium-gold/30 hover:border-premium-gold transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-premium-navy rounded-full flex items-center justify-center">
                  <i className="ri-error-warning-line text-xl text-premium-gold"></i>
                </div>
                <h2 className="text-2xl font-bold text-premium-navy font-display">Задача</h2>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4 font-body">
                Преодолеть «стеклянный потолок» продаж, оптимизировать расходы на рекламу и масштабировать присутствие бренда снеков для здорового питания на трёх ключевых маркетплейсах одновременно.
              </p>
              <div className="bg-premium-pearl border-2 border-premium-gold/30 rounded-xl p-4">
                <p className="text-sm text-premium-navy font-semibold mb-1 font-body">Проблема клиента:</p>
                <p className="text-sm text-gray-700 font-body">
                  Продажи вышли на плато. Рекламный бюджет расходовался, но выручка не росла. Требовался системный пересмотр стратегии на всех площадках.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-premium-gold/30 hover:border-premium-gold transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-premium-navy rounded-full flex items-center justify-center">
                  <i className="ri-time-line text-xl text-premium-gold"></i>
                </div>
                <h2 className="text-2xl font-bold text-premium-navy font-display">Контекст проекта</h2>
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-4 bg-premium-pearl rounded-xl p-4">
                  <div className="w-10 h-10 bg-premium-navy rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-leaf-line text-xl text-premium-gold"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-premium-navy font-body">Ниша</p>
                    <p className="text-sm text-gray-600 font-body">Снеки для здорового питания (FMCG)</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-premium-pearl rounded-xl p-4">
                  <div className="w-10 h-10 bg-premium-navy rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-store-line text-xl text-premium-gold"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-premium-navy font-body">Площадки</p>
                    <p className="text-sm text-gray-600 font-body">Wildberries, Ozon, Яндекс Маркет</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 bg-premium-navy rounded-xl p-4 border-2 border-premium-gold/30">
                  <div className="w-10 h-10 bg-premium-gold rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-xl text-premium-navy"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white font-body">Срок работы</p>
                    <p className="text-2xl font-bold text-premium-gold font-display">3 месяца</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* System Actions */}
          <div className="bg-premium-navy rounded-2xl p-8 md:p-12 text-white mb-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 border border-premium-gold/10 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 border border-premium-gold/10 rounded-full -ml-24 -mb-24"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-4 text-center font-display">⚙️ Что именно мы сделали? (The System)</h2>
              <p className="text-xl text-premium-gold/80 text-center mb-10 font-body">
                Рост — это не удача, а результат чёткой последовательности действий
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {systemActions.map((action, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border-2 border-premium-gold/20 hover:border-premium-gold/40">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-premium-gold rounded-full flex items-center justify-center flex-shrink-0">
                        <i className={`${action.icon} text-2xl text-premium-navy`}></i>
                      </div>
                      <div>
                        <h5 className="text-xl font-bold mb-2 font-display">{action.title}</h5>
                        <p className="text-premium-gold/80 text-sm leading-relaxed font-body">{action.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Omnichannel note */}
              <div className="mt-8 bg-white/10 backdrop-blur-sm rounded-xl p-6 border-2 border-premium-gold/30">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-premium-gold rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-links-line text-2xl text-premium-navy"></i>
                  </div>
                  <div>
                    <h5 className="text-xl font-bold mb-2 font-display">Омниканальная стратегия</h5>
                    <p className="text-premium-gold/80 text-sm leading-relaxed font-body">
                      Мы внедрили единую стратегию, работая параллельно на трёх ключевых площадках. Это позволило охватить максимальную аудиторию и создать синергетический эффект: рост на одной площадке усиливал позиции на других.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-premium-navy mb-4 text-center font-display">
              📊 Результаты в цифрах (Growth Metrics)
            </h2>
            <p className="text-center text-gray-600 mb-12 text-lg font-body">
              Мы внедрили омниканальную стратегию, работая параллельно на трёх ключевых площадках
            </p>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {platformResults.map((result, index) => (
                <div key={index} className="bg-premium-pearl rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-premium-gold/30 hover:border-premium-gold">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-premium-navy rounded-full flex items-center justify-center">
                      <i className={`${result.icon} text-2xl text-premium-gold`}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-premium-navy font-display">{result.platform}</h3>
                  </div>

                  {/* Bars */}
                  <div className="space-y-5 mb-6">
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-600 font-body">Было</span>
                        <span className="text-sm font-bold text-gray-700 font-body">{formatNum(result.before)} ₽</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden">
                        <div
                          className="bg-gray-400 h-full rounded-full"
                          style={{ width: `${(result.before / maxValue) * 100}%` }}
                        ></div>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-600 font-body">Стало</span>
                        <span className="text-sm font-bold text-premium-navy font-body">{formatNum(result.after)} ₽</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-8 overflow-hidden">
                        <div
                          className="bg-premium-gold h-full rounded-full"
                          style={{ width: `${(result.after / maxValue) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  {/* Growth Badge */}
                  <div className="bg-premium-navy rounded-xl p-4 text-center border-2 border-premium-gold/30">
                    <div className="text-white">
                      <div className="text-sm font-medium mb-1 font-body">Рост выручки</div>
                      <div className="text-4xl font-bold text-premium-gold font-display">{result.growth}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary bar */}
            <div className="bg-premium-navy rounded-2xl p-8 text-white text-center mb-10 border-2 border-premium-gold/30">
              <p className="text-premium-gold/80 text-sm font-medium mb-2 font-body">Суммарный рост выручки по всем площадкам</p>
              <div className="text-5xl font-bold mb-2 font-display">
                {formatNum(platformResults.reduce((s, r) => s + r.before, 0))} ₽
                <span className="text-premium-gold mx-4">→</span>
                {formatNum(platformResults.reduce((s, r) => s + r.after, 0))} ₽
              </div>
              <p className="text-premium-gold/80 text-lg mt-2 font-body">
                Общий рост: <strong className="text-premium-gold">+{Math.round(((platformResults.reduce((s, r) => s + r.after, 0) / platformResults.reduce((s, r) => s + r.before, 0)) - 1) * 100)}%</strong> за 3 месяца
              </p>
            </div>

            {/* Key Achievement */}
            <div className="bg-premium-pearl border-2 border-premium-gold rounded-2xl p-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-premium-gold rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-trophy-line text-2xl text-premium-navy"></i>
                </div>
                <div>
                  <h3 className="font-bold text-premium-navy mb-3 text-xl font-display">💡 Вывод</h3>
                  <div className="space-y-2 text-sm text-gray-700 font-body">
                    <p>
                      <strong>Омниканальный подход позволил преодолеть «стеклянный потолок» продаж.</strong> Работая параллельно на трёх площадках, мы создали синергетический эффект: каждая площадка усиливала другие.
                    </p>
                    <p>
                      Глубокая аналитика + пересмотр юнит-экономики + точечная реклама = рост от +113% до +180% в зависимости от площадки.
                    </p>
                    <p className="text-premium-navy font-semibold">
                      Результат: за 3 месяца суммарная выручка выросла с {formatNum(platformResults.reduce((s, r) => s + r.before, 0))} ₽ до {formatNum(platformResults.reduce((s, r) => s + r.after, 0))} ₽.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center py-12 bg-premium-navy rounded-3xl border-2 border-premium-gold/30 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 border border-premium-gold/10 rounded-full -mr-24 -mt-24"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 border border-premium-gold/10 rounded-full -ml-20 -mb-20"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl font-bold text-white mb-4 font-display">Хотите повторить этот результат?</h2>
              <p className="text-premium-gold/80 text-lg mb-8 max-w-2xl mx-auto font-body">
                Рассчитаем потенциал роста вашего проекта на маркетплейсах и выстроим омниканальную стратегию.
              </p>
              <Link
                to="/#lead-form"
                className="inline-block bg-premium-gold text-premium-navy px-10 py-5 rounded-full text-xl font-bold hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer font-body"
              >
                Рассчитать потенциал роста моего проекта
              </Link>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}